import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import { ArrowLeft, RefreshCw, ExternalLink, Truck, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { FulfillmentStatusBadge, FulfillmentSubmitDialog } from '../components'
import { useFulfillmentOrder, useRefreshFulfillment, useCancelFulfillment } from '../hooks/useFulfillment'

function money(amount: any, currency: string | null) {
  if (amount === null || amount === undefined) return '—'
  return `${currency || ''} ${Number(amount).toFixed(2)}`.trim()
}

function fmt(d: string | null) {
  return d ? format(new Date(d), 'dd MMM yyyy, HH:mm') : '—'
}

export function FulfillmentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const orderId = id ? parseInt(id, 10) : null
  const { data, isLoading } = useFulfillmentOrder(orderId)
  const refresh = useRefreshFulfillment()
  const cancel = useCancelFulfillment()

  if (isLoading || !data) {
    return <div className="flex justify-center py-16"><LoadingSpinner /></div>
  }

  const { order, items, shipments } = data
  const addr = order.recipient?.address
  // Cancel is only meaningful for a live provider order not already terminal.
  const canCancel = !!order.providerOrderId && !['CANCELLED', 'COMPLETE'].includes(order.status)

  const onCancel = () => {
    if (orderId && window.confirm('Cancel this order with the provider? This attempts a best-effort cancel and re-syncs status.')) {
      cancel.mutate(orderId)
    }
  }

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/fulfillment')}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <div>
            <h1 className="font-serif text-2xl font-semibold tracking-tight">
              {order.providerOrderId || `Order ${order.id}`}
            </h1>
            <p className="text-sm text-muted-foreground">
              {order.provider} · Shopify #{order.merchantReference || '—'}
              {order.shopifyOrderId ? ` (id ${order.shopifyOrderId})` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <FulfillmentStatusBadge status={order.status} />
          <FulfillmentSubmitDialog
            shopifyOrderId={order.shopifyOrderId}
            merchantReference={order.merchantReference}
            disabled={!order.shopifyOrderId}
          />
          {canCancel && (
            <Button variant="outline" size="sm" onClick={onCancel} disabled={cancel.isPending}>
              <XCircle className={`mr-1 h-4 w-4 ${cancel.isPending ? 'animate-pulse' : ''}`} />
              Cancel
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => orderId && refresh.mutate(orderId)}
            disabled={refresh.isPending || !order.providerOrderId}
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${refresh.isPending ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Issues */}
      {order.issues && order.issues.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-red-800">Issues</CardTitle></CardHeader>
          <CardContent className="text-sm text-red-800">
            <ul className="list-disc pl-5">
              {order.issues.map((iss: any, i: number) => (
                <li key={i}>{iss.errorCode}: {iss.description}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Summary grid */}
      <div className="grid gap-5 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Recipient</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div className="font-medium">{order.recipient?.name || '—'}</div>
            <div className="text-muted-foreground">{order.recipient?.email || ''}</div>
            {addr && (
              <div className="text-muted-foreground">
                {[addr.line1, addr.line2, addr.townOrCity, addr.stateOrCounty, addr.postalOrZipCode, addr.countryCode]
                  .filter(Boolean).join(', ')}
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Order</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="Shipping" value={order.shippingMethod || '—'} />
            <Row label="Stage" value={order.stage || '—'} />
            <Row label="Cost" value={money(order.totalCost, order.currency)} />
            <Row label="Tax" value={money(order.totalTax, order.currency)} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Timeline</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row label="Created" value={fmt(order.providerCreatedAt)} />
            <Row label="Updated" value={fmt(order.providerUpdatedAt)} />
            <Row label="Completed" value={fmt(order.completedAt)} />
          </CardContent>
        </Card>
      </div>

      {/* Items */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Items ({items.length})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16" />
                <TableHead>Provider SKU</TableHead>
                <TableHead>Paper</TableHead>
                <TableHead>Attributes</TableHead>
                <TableHead className="text-center">Copies</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell>
                    {it.thumbnailUrl
                      ? <img src={it.thumbnailUrl} alt="" className="h-10 w-10 rounded object-cover" />
                      : <div className="h-10 w-10 rounded bg-muted" />}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{it.providerSku}</TableCell>
                  <TableCell className="text-xs">
                    {it.paperType ? `${it.paperType}${it.substrateWeight ? ` ${it.substrateWeight}` : ''}` : '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {it.attributes && Object.keys(it.attributes).length
                      ? Object.entries(it.attributes).map(([k, v]) => `${k}: ${v}`).join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell className="text-center">{it.copies}</TableCell>
                  <TableCell className="text-right">{money(it.itemCost, it.currency)}</TableCell>
                  <TableCell className="text-xs">{it.status || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Shipments */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Shipments ({shipments.length})</CardTitle></CardHeader>
        <CardContent>
          {shipments.length === 0 ? (
            <p className="text-sm text-muted-foreground">No shipments yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Carrier</TableHead>
                  <TableHead>Tracking</TableHead>
                  <TableHead>Lab</TableHead>
                  <TableHead>Dispatched</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shipments.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1">
                        <Truck className="h-3 w-3 text-muted-foreground" />
                        {[s.carrierName, s.carrierService].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.trackingNumber
                        ? (s.trackingUrl
                          ? <a href={s.trackingUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">{s.trackingNumber}<ExternalLink className="h-3 w-3" /></a>
                          : s.trackingNumber)
                        : '—'}
                    </TableCell>
                    <TableCell className="text-xs">{[s.fulfillmentLab, s.fulfillmentCountry].filter(Boolean).join(' / ') || '—'}</TableCell>
                    <TableCell className="text-xs">{fmt(s.dispatchDate)}</TableCell>
                    <TableCell className="text-xs">{s.status || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  )
}
